import { useCallback } from 'react';

import { useBooleanState } from '@components/component-helpers';
import { FieldTypeKind, FormV2, InputKind, createFieldOfKind } from '@components/form/v2';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeFieldType } from 'api/attributes/requests';
import { useCreateJourneyForm } from 'api/journey-forms/hooks';
import { useJourney, useJourneyAttributes } from 'api/journeys/hooks';

type useCreateDefaultJourneyFormProps = {
  journeyShortId: string;
};
export const useCreateDefaultJourneyForm = ({
  journeyShortId,
}: useCreateDefaultJourneyFormProps) => {
  const journeyQuery = useJourney(journeyShortId);
  const jtaQuery = useJourneyAttributes(journeyShortId);
  const ctaQuery = useCompanyTravellerAttributeTemplates({ systemAttributes: true });
  const createMutation = useCreateJourneyForm(journeyShortId);
  const activeState = useBooleanState(true);

  const createDefault = useCallback(async () => {
    if (!journeyQuery.isSuccess || !ctaQuery.isSuccess || !jtaQuery.isSuccess) {
      return null;
    }

    const journey = journeyQuery.data;

    const requiredCta = ctaQuery.data.filter(
      (attribute) =>
        attribute.isRequired && attributeTypeToFieldType[attribute.attributeType] !== null
    );

    const ctaRequiredDefaults = requiredCta.map((attribute) => {
      const kind = attributeTypeToFieldType[attribute.attributeType] as InputKind;
      const isRequiredSystemAttribute =
        attribute.isSystemAttribute &&
        (attribute.title === 'Full name' || attribute.title === 'Email');
      return createFieldOfKind(kind, {
        label: attribute.templateTitle,
        isRequired: true,
        companyAttributeId: attribute.shortId,

        ...(kind === 'input-file' && {
          allowMultiple: attribute.attributeType === 'file_multi',
        }),
        ...(kind === 'input-number' && {
          isDecimal: attribute.attributeType === 'decimal',
        }),
        builderProps: {
          canEditRequired: !isRequiredSystemAttribute,
          canRemove: !isRequiredSystemAttribute,
          canEditKind: false,
          canEditConditions: !isRequiredSystemAttribute,
          canDuplicate: !isRequiredSystemAttribute,
          canEditDefaultValue: !isRequiredSystemAttribute,
        },

        // @ts-expect-error if it isnt a dropdown then it will be undefined anyway
        optionsFromListId: attribute.options,
      });
    });

    const requiredJta = jtaQuery.data.filter(
      (attribute) =>
        attribute.isActive &&
        attribute.isRequired &&
        attributeTypeToFieldType[attribute.attributeType] !== null
    );
    const jtaRequiredDefaults = requiredJta.map((attribute) => {
      const kind = attributeTypeToFieldType[attribute.attributeType] as InputKind;
      return createFieldOfKind(kind, {
        label: attribute.title,
        isRequired: attribute.isRequired,
        journeyAttributeId: attribute.shortId,

        ...(kind === 'input-file' && {
          allowMultiple: attribute.attributeType === 'file_multi',
        }),
        ...(kind === 'input-number' && {
          isDecimal: attribute.attributeType === 'decimal',
        }),
        // @ts-expect-error if it isnt a dropdown then it will be undefined anyway
        optionsFromListId: attribute.options,
      });
    });

    const defaultForm: FormV2 = {
      schema: 'v2',
      fields: [
        createFieldOfKind('heading', {
          label: journey.title,
        }),
        ...ctaRequiredDefaults,
        ...jtaRequiredDefaults,
        createFieldOfKind('submit', {
          builderProps: {
            canRemove: false,
            canDuplicate: false,
            canReorder: false,
          },
        }),
      ],
    };

    const defaultSubmissionMessage: FormV2 = {
      schema: 'v2',
      fields: [
        createFieldOfKind('heading', { label: 'Form received' }),
        createFieldOfKind('html', {
          content:
            '<p>Thank you for taking the time to complete this important form. We will contact you if we feel we need more information.</p>\n',
        }),
      ],
    };

    const form = {
      fieldsConfig: defaultForm,
      name: journey.title,
      description: '',
      submissionMessageConfig: defaultSubmissionMessage,
    };

    const response = await createMutation.mutateAsync({
      ...form,
      isAutoStart: true,
      isPortalForm: false,
      isPublished: activeState.value,
    });
    return response.shortId;
  }, [
    activeState.value,
    createMutation,
    ctaQuery.data,
    ctaQuery.isSuccess,
    journeyQuery.data,
    journeyQuery.isSuccess,
    jtaQuery.data,
    jtaQuery.isSuccess,
  ]);

  return {
    createDefault,
    isLoading:
      createMutation.isLoading ||
      journeyQuery.isLoading ||
      jtaQuery.isLoading ||
      ctaQuery.isLoading,
  };
};

export const attributeTypeToFieldType: Record<AttributeFieldType, FieldTypeKind | null> = {
  single_select: 'input-dropdown',
  date: 'input-date',
  email: 'input-email',
  multi_select: 'input-multiple-choice',
  paragraph: 'input-text-large',
  phone_number: 'input-phone',
  text: 'input-text-small',
  file: 'input-file',
  file_multi: 'input-file',
  decimal: 'input-number',
  number: 'input-number',

  // TODO
  boolean: null,
  user: null,
};
